/**
 * The ledger · a learner's own record, drawn as a commit history.
 *
 * Worlds are derived from consecutive missions that share a `world` value, in
 * level order, so adding a mission with a new world name adds a section to the
 * ledger without touching this file. Each level is a node on a hairline rail,
 * because the shape of the course should be the shape of the thing the course
 * teaches.
 *
 * Motion here has one job: make progress feel physical. Rows arrive in the
 * order you will play them, the level you are on breathes, the numbers count
 * rather than jump, and the rank meter ticks from where it was to where a
 * clear just put it. Nothing decorates.
 *
 * ## What it stopped being
 *
 * This was the whole of `/` — a hero, a terminal window, the pitch, the
 * ledger, and the credibility bands under it, for everybody. The pitch half is
 * components/home/Landing.tsx now, and this is what is left: the screen for
 * somebody who has already been sold.
 *
 * It is reached by address rather than by inference. `/` is the landing page
 * for everybody, and this lives at `/?view=course`, which is what every
 * "home" link in the app and the mark's own default point at (lib/landing.ts,
 * `COURSE_HREF`). It briefly worked the other way — `/` was this page for any
 * browser holding a record — and the cost of that was a front page almost
 * nobody could see.
 *
 * Three things went with it, and each was costing this reader something:
 *
 * - **The hero**, replaced by the Up next card below. A learner coming back
 *   from a level does not need to be told what EngineerOS is; they need to be
 *   told where they were. Same button, same sentence under it — the whole of
 *   `ctaLabel` and `ctaNote` is unchanged — in a card that names the chapter
 *   and the level rather than the product.
 * - **The terminal window**, which is a replay of Level 0 and belongs on the
 *   page read by somebody who has not played it (components/home/shell.ts).
 * - **Pedigree and Proof.** A learner thirteen levels in has checked this
 *   product by using it. `/about` — the footer link — is how the argument
 *   stays reachable forever, and the landing page is where it lives.
 *
 * `Offer` stays. It is not a pitch for the product, it is the price of the
 * half of it this reader does not own, and the foot of their own ledger is
 * exactly where that belongs.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MotionConfig, motion, useReducedMotion, type Variants } from 'motion/react';
import type { HomeProps } from '../contracts';
import type { PublicMission } from '../../types';
import { levelProgress, rankFor, totals } from '../../progress';
import { gateFor, ownedCount, paidSpan } from '../../lib/entitlement';
import { Brand, Button, Counter, LinkButton, Meter, Parts, Tag } from '../../ui';
import { prefersReducedMotion } from '../../lib/motion';
import { num } from '../../lib/format';
import { useLocale, useStrings } from '../../state/useLocale';
import { legalHref } from '../../lib/legal';
import { ABOUT_PATH } from '../../lib/landing';
import { sound } from '../../lib/sound';
import { Account } from './Account';
import { LevelRow } from './LevelRow';
import { Certificates } from './Certificates';
import { PracticeReview } from './PracticeReview';
import { AGENT_PATH } from '../../lib/agent-keys';
import { closestCertificate, useCertificates } from '../../lib/certificates';
import { Offer } from './Offer';
import { PreviewResults } from './PreviewResults';
import { TermNote } from './TermNote';
import { openerForWorld, openerSeen } from '../../lib/openers';

/* Rows arrive 18 ms apart, but the cascade is capped: however many levels the
   course grows to, the last row is on screen well inside half a second. */
const ROW_STEP = 0.018;
const MAX_CASCADE = 0.42;

interface World {
  name: string;
  /**
   * The levels, and only the levels. Everything in the header counts this and
   * not the checkpoint below it — see {@link buildWorlds}.
   */
  missions: PublicMission[];
  /** The assessment for this world, drawn under its levels. Null until one is authored. */
  checkpoint: PublicMission | null;
  /** Index of this world's first row in the whole ledger, for the cascade. */
  start: number;
}

/**
 * The ledger's sections: the worlds in catalogue order, each with the
 * checkpoint that assesses it drawn at its foot.
 *
 * ## Why this is two passes and not a reduce
 *
 * Worlds are runs of consecutive missions sharing a `world` value, which is
 * what lets a new chapter appear on the map without touching this file. A
 * checkpoint cannot join that run. It is its own track, numbered in its own
 * sequence, and it carries `world: Checkpoints` because the catalogue refuses
 * a world entered twice (docs/checkpoints.md, "Where a checkpoint lives in
 * the catalogue"). Grouped by the plain rule it therefore lands where it sits
 * in the file — all twenty-five of them in one block after the general track,
 * a hundred-odd rows below the worlds they belong to.
 *
 * That is the wrong place for the only thing on the page that is not a level.
 * A checkpoint unlocks the moment its world's last level is cleared, and a
 * thing that unlocks out of sight has not really unlocked. Worse, a world
 * whose levels are all cleared folds to its heading, so the course closed the
 * chapter and filed what the chapter earned at the bottom of the page. And a
 * block of twenty-five exams at the end reads as a final gauntlet, which is
 * the one thing rule 1 spends its length denying.
 *
 * So: the catalogue keeps its order and the screen does not, and `assesses` is
 * the field that reconciles them. Pass one groups the levels, skipping the
 * checkpoint track. Pass two puts each checkpoint against the world it names.
 *
 * ## A checkpoint the map cannot place still gets drawn
 *
 * If `assesses` is missing or names no world on this page, the checkpoint
 * keeps a section of its own under its catalogue `world`, exactly as before.
 * The failure that shows an assessment in an odd place is recoverable; the
 * one that silently drops it from a paid course is not. This is not
 * hypothetical — `assesses` was absent from every locked checkpoint stub
 * until the build was taught to carry it, and that is a shape a stale
 * catalogue in somebody's cache can still have.
 */
function buildWorlds(missions: PublicMission[]): World[] {
  const out: World[] = [];
  const checkpoints: PublicMission[] = [];

  for (const m of missions) {
    if (m.track === 'checkpoint') {
      checkpoints.push(m);
      continue;
    }
    const last = out[out.length - 1];
    if (last && last.name === m.world) last.missions.push(m);
    else out.push({ name: m.world, missions: [m], checkpoint: null, start: 0 });
  }

  for (const cp of checkpoints) {
    // `find`, not the first empty slot: two checkpoints naming one world is
    // an authoring mistake, and the second of them should fall through to a
    // section of its own where it is visible, not quietly overwrite the first.
    const home = cp.assesses ? out.find((w) => w.name === cp.assesses && w.checkpoint === null) : undefined;
    if (home) home.checkpoint = cp;
    else out.push({ name: cp.world, missions: [], checkpoint: cp, start: 0 });
  }

  // The cascade counts rows in the order they are drawn, which is no longer
  // the order they appear in the catalogue: every checkpoint moved, and each
  // one it passed shifted a place. Read off the built list rather than the
  // input, or the rows arrive out of step from the first world that has one.
  let row = 0;
  for (const w of out) {
    w.start = row;
    row += w.missions.length + (w.checkpoint ? 1 : 0);
  }
  return out;
}

/* Fifty levels in fourteen worlds is a long page, and most of it is a record
   of work already done. A world every level of which is cleared folds to its
   heading — the count and the full bar still on it, so nothing is hidden,
   only closed — and the ledger shortens as the learner gets further into it.
   The world holding the level they are on never folds, whatever else is true
   of it: that is the one they came back for.

   `open` is the learner's own answer, once they have given one, and it
   outranks both rules in both directions. It is kept per browser (below), so
   a learner who closed a chapter finds it closed on their next visit. Only
   chapters they actually clicked are remembered; every other one still
   follows the rules, so a world they never touched still folds the day they
   finish it. */
function worldIsFolded(open: Record<string, boolean>, name: string, cleared: number, total: number, here: boolean) {
  const chosen = open[name];
  if (chosen !== undefined) return !chosen;
  return here ? false : total > 0 && cleared === total;
}

/* Per browser and not part of progress, like an opener marked read: it says
   nothing about what the learner knows, and losing it costs a click. Keyed by
   the English `world:` name, which never changes with the language. */
const FOLDS_KEY = 'engineeros.ledger.open';

function loadOpenWorlds(): Record<string, boolean> {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(FOLDS_KEY) ?? '{}');
    if (saved === null || typeof saved !== 'object' || Array.isArray(saved)) return {};
    return Object.fromEntries(Object.entries(saved).filter(([, v]) => typeof v === 'boolean'));
  } catch {
    return {};
  }
}

function saveOpenWorlds(open: Record<string, boolean>): void {
  try {
    localStorage.setItem(FOLDS_KEY, JSON.stringify(open));
  } catch {
    /* private mode, or storage disabled */
  }
}

/** Each world's list continues the single cascade running down the page. */
const listVariants = (start: number, step: number): Variants => ({
  initial: {},
  animate: { transition: { delayChildren: start * step, staggerChildren: step } },
});

/* Two digits so the chapter numbers form a column. `num` rather than
   `String`, because every number on this page goes through one formatter —
   and it pins Western digits in both languages, so the padding still holds. */
const pad2 = (n: number) => num(n).padStart(2, '0');

/* What this screen last showed. The ledger remounts every time the learner
   comes back from a level, so this memory has to live outside the component;
   a page load starts it over, which is what "once per session" means
   everywhere else here. The strip compares against `shownXp` to tell an
   advance from a page simply re-entered: higher is an advance and ticks; the
   first look of the session, the same figure, or a lower one after an account
   switch all render settled. */
let shownXp: number | null = null;

export function Ledger({
  missions,
  progress,
  next,
  openers,
  driverLabel,
  account,
  sync,
  onBackup,
  onLocked,
  onUnlock,
  offer,
  onInvite,
}: HomeProps) {
  const s = useStrings();
  // The legal pages are static files outside the bundle, so the link has to
  // name the language rather than the page reading it (lib/legal.ts).
  const { locale } = useLocale();
  // useReducedMotion tracks the media query live; prefersReducedMotion() covers
  // the render before the hook has resolved.
  const reduced = useReducedMotion() ?? prefersReducedMotion();

  // Decided on the render that mounts and held for the life of the mount, so
  // a toast re-rendering the page under the strip cannot cut a tick short.
  const arrival = useRef<{ seenXp: number | null } | null>(null);
  if (arrival.current === null) arrival.current = { seenXp: shownXp };
  const { seenXp } = arrival.current;

  useEffect(() => {
    shownXp = progress.xp;
  }, [progress.xp]);

  const worlds = useMemo<World[]>(() => buildWorlds(missions), [missions]);

  const step = missions.length > 1 ? Math.min(ROW_STEP, MAX_CASCADE / (missions.length - 1)) : ROW_STEP;

  const [openWorlds, setOpenWorlds] = useState<Record<string, boolean>>(loadOpenWorlds);
  const toggleWorld = useCallback((name: string, folded: boolean) => {
    sound.click();
    setOpenWorlds((prev) => {
      const next = { ...prev, [name]: folded };
      saveOpenWorlds(next);
      return next;
    });
  }, []);

  const { current: rank, next: nextRank, pct } = rankFor(progress.xp);
  // A new rank ticks from empty and the name cuts to green (motion.css §6);
  // the same rank ticks from where the strip last left it.
  const before = seenXp !== null && progress.xp > seenXp ? rankFor(seenXp) : null;
  const promoted = before !== null && before.current.name !== rank.name;
  const meterFrom = before === null ? undefined : promoted ? 0 : before.pct;
  const t = totals(progress, missions);
  // "Cleared everything" and "cleared everything you hold" are two different
  // states now, and only the first of them is the end of the course. A
  // learner who finishes the free twenty with thirty still locked must not be
  // congratulated on completing a course they have most of left.
  //
  // `>=` rather than `===`: somebody who bought the course, cleared past
  // level 19 and then lapsed keeps those clears in their record, so the count
  // can exceed what they currently hold.
  const { owned } = ownedCount(missions);
  // The paid half, read off the catalogue once and spent on every sentence
  // that names it: the hero's button, the note under it, and the tooltip on
  // each paywalled row. Null is "nothing is locked" — this account has the
  // course, or this build has no paid half — which is the question the first
  // locked mission used to answer on its own.
  //
  // It also carries the end of the course, which the hero was working out as
  // `total - 1`: true only while the levels are numbered from zero with no
  // gap in them. They are today, and the ledger should not be the place that
  // finds out when they are not.
  //
  // `range` inside it is allowed to be absent, and every sentence built from
  // this has a second form for when it is. A range is a claim that the paid
  // half runs from one number to another in the lane the reader is looking
  // at, and that claim stops being true the day a track is priced whole —
  // `count` is the half of this that is always true (lib/entitlement.ts).
  const paid = paidSpan(missions);
  const ownedCleared = owned > 0 && t.cleared >= owned;
  const allCleared = ownedCleared && paid === null;
  const moreToUnlock = ownedCleared && paid !== null;

  const ctaMission = next ?? (ownedCleared ? missions[0] ?? null : null);
  const ctaHref = ctaMission ? `?mission=${encodeURIComponent(ctaMission.id)}` : '?';
  // One whole sentence per state and per language, rather than a clause
  // stitched to a level number at the call site: the number does not sit in
  // the same place in both languages, and "Start"/"Continue" is not a word
  // that can be swapped into a frame elsewhere.
  //
  // The hero quotes the range whenever the catalogue reports one and counts
  // when it does not. It asks nothing about which lane the range belongs to,
  // unlike the rows below: `paidSpan` returns a range only when *every*
  // locked level is one lane's tail, so a range at all means there is one
  // paid lane, the learner is in it, and its numbers are the numbers under
  // their cursor (lib/entitlement.ts).
  const ctaLabel = next
    ? t.cleared === 0
      ? s.home.hero.ctaStart(next.level)
      : s.home.hero.ctaContinue(next.level)
    : moreToUnlock && paid
      ? paid.range
        ? s.home.hero.ctaUnlock(paid.range.from, paid.range.to)
        : s.home.hero.ctaUnlockCount(paid.count)
      : allCleared
        ? s.home.hero.ctaReplay
        : s.home.hero.ctaIdle;
  const ctaNote = next
    ? s.home.hero.noteNext(next.level, next.estimated_minutes)
    : moreToUnlock && paid
      ? paid.range
        ? s.home.hero.noteUnlock(owned, paid.range.from, paid.range.to)
        : s.home.hero.noteUnlockCount(owned, paid.count)
      : allCleared
        ? s.home.hero.noteReplay
        : s.home.hero.noteIdle;

  // The chapter the next level is in, named the way every other world name
  // on this page is: `world` is the English key three places join on, and the
  // catalogue is where it becomes something to read (docs/i18n.md §6.1).
  const nextChapter = next ? (s.home.worlds[next.world]?.name ?? next.world) : '';

  const rankNote = nextRank
    ? s.home.standing.toNextRank(nextRank.xp - progress.xp, nextRank.name)
    : s.home.standing.topRank;
  const driverOff = /offline|error|unavailable/i.test(driverLabel);

  function handleBackup() {
    sound.click();
    onBackup();
  }

  function handleSignIn() {
    sound.click();
    account.signIn();
  }

  /* Said once the learner has something to lose, and only while nothing else
     is keeping it. It is a statement of where the record is, not a warning
     and not a thing to dismiss: it carries no close button because it goes
     away by itself the moment either action is taken. */
  const localOnly = !account.user && t.cleared > 0;

  // Read once per page load and shared with the clear overlay, which
  // refreshes it after a checkpoint pass (lib/certificates.ts). The strip
  // draws all of it; the offer wants one line from it, and only for an
  // account that does not own the course.
  const certificates = useCertificates(account.user?.id ?? null);
  const closest = certificates && !certificates.owns ? closestCertificate(certificates) : null;

  return (
    <MotionConfig reducedMotion="user">
      <div className="home">
        <header className="bar">
          <Brand />
          <div className="bar-end">
            {/* The pip is the one thing in the bar that is a live reading
                rather than a label: it is there when the emulator is up and
                gone when it is not, so "ready" is never a word the page is
                merely asserting. */}
            <span className={driverOff ? 'tag tag-off' : 'tag tag-live'} id="map-driver">
              {!driverOff && <span className="tag-pip" aria-hidden="true" />}
              {driverLabel}
            </span>
            {account.user && sync === 'error' && (
              <span className="tag tag-off" title={s.home.bar.notSyncedWhy}>
                {s.home.bar.notSynced}
              </span>
            )}
            <Account account={account} />
            {/* Beside the account and offered whether or not there is one: the
                record on the other side of this link is this browser's, and a
                learner who never signs in has exactly as much of one. */}
            <LinkButton variant="quiet" className="to-profile" href="?profile=me">
              {s.profile.open}
            </LinkButton>
            {/* And beside the record, the agent the course is about directing:
                the agent page, where a learner makes the key that connects
                their own agent (components/agent/AgentPage.tsx). It took this
                place from the paid task board when paid tasks retired on
                2026-10-31.

                An absolute path, not a `?` route — so the delegated click
                handler above ignores it and the browser navigates to a page
                that boots no virtual machine. This bar is only ever on the
                map, so that costs nobody a level they were in the middle of. */}
            <LinkButton variant="quiet" className="to-agent" href={AGENT_PATH}>
              {s.agents.title}
            </LinkButton>
          </div>
        </header>

        {/* Where you were.
            ------------------------------------------------------------------
            This is the whole of what replaced the hero, and the swap is the
            point of the split: a learner arriving here has already decided
            what this product is, and the first thing on their screen should be
            the thing they came back for.

            Every sentence in it is the one the hero used — `ctaLabel` and
            `ctaNote` are unchanged and still carry all four states, including
            the two where there is no next level. What is new is the frame
            around them: the chapter, the level number and its title, so the
            card says *which* work rather than only "continue".

            The green left edge is the same 2px rule `.lvl.is-current` draws on
            the row further down the page, so the ledger says "here" in one
            vocabulary rather than two. */}
        <section className="upnext" aria-label={s.home.upnext.label}>
          <div className="upnext-where">
            <p className="label">{s.home.upnext.label}</p>
            {/* With a level to play, the card is that level: its title is the
                heading, and the sentence under the button carries the minutes
                and the XP. With none, there is no title to print that the
                button would not be printing again a second time — so the
                state's own sentence moves over here and the button keeps only
                its label. */}
            {next ? (
              <>
                <h1 className="upnext-title">{next.title}</h1>
                <p className="upnext-place">{s.home.upnext.place(nextChapter, next.level)}</p>
              </>
            ) : (
              <p className="upnext-done">{ctaNote}</p>
            )}
          </div>
          <p className="upnext-go">
            {/* With a price to show, the price first: the offer band below
                says what the course costs and how to buy it, and a visitor
                who pressed this straight into checkout never saw a number
                (visitor runs, 2026-10-29). Without one, a button rather than a
                link: buying is an action with a side effect, not a place to
                go. Every other state is still a link to a level. */}
            {moreToUnlock && offer?.price ? (
              <LinkButton variant="solid" size="lg" href="#pricing">
                {ctaLabel}
              </LinkButton>
            ) : moreToUnlock ? (
              <Button variant="solid" size="lg" onClick={() => onUnlock()}>
                {ctaLabel}
              </Button>
            ) : (
              <LinkButton variant="solid" size="lg" href={ctaHref}>
                {ctaLabel}
              </LinkButton>
            )}
            {next && <span className="note">{ctaNote}</span>}
          </p>
        </section>

        <section className="standing" aria-label={s.home.standing.label}>
          <div className="standing-rank">
            <span className="label">{s.home.standing.rank}</span>
            <strong className={promoted ? 'rank is-new' : 'rank'}>{rank.name}</strong>
            <span className="note">{rank.blurb}</span>
          </div>
          <div className="standing-meter">
            <Meter pct={pct} from={meterFrom} label={s.home.standing.rankMeter} />
            <span className="note">{rankNote}</span>
          </div>
          <dl className="figures">
            <div className="figure">
              <dt>{s.home.standing.xp}</dt>
              <dd>
                <em>
                  <Counter value={progress.xp} />
                </em>
              </dd>
            </div>
            <div className="figure">
              <dt>{s.home.standing.levels}</dt>
              <dd>
                {/* The counter is an element, so "3/50" is parts: the figure
                    and its total are one reading, and where the total sits
                    is the catalogue's to say. */}
                <Parts
                  of={s.home.standing.outOf(
                    <em>
                      <Counter value={t.cleared} />
                    </em>,
                    missions.length,
                  )}
                />
              </dd>
            </div>
            <div className="figure">
              <dt>{s.home.standing.stars}</dt>
              <dd>
                <Parts
                  of={s.home.standing.outOf(
                    <em>
                      <Counter value={t.stars} />
                    </em>,
                    t.maxStars,
                  )}
                />
              </dd>
            </div>
          </dl>
          {localOnly && (
            <div className="standing-local">
              <Tag>{s.home.standing.localOnly}</Tag>
              <span className="note">{s.home.standing.localOnlyWhy}</span>
              {account.enabled && (
                <Button variant="quiet" onClick={handleSignIn} disabled={account.busy}>
                  {account.busy ? s.home.standing.signingIn : s.home.standing.signIn}
                </Button>
              )}
              <Button variant="quiet" onClick={handleBackup} title={s.home.bar.restoreCodeWhy}>
                {s.home.bar.restoreCode}
              </Button>
            </div>
          )}
        </section>

        <Certificates value={certificates} />
        <PracticeReview key={account.user?.id ?? 'guest'} userId={account.user?.id ?? null}
          clearedAt={(id) => levelProgress(progress, id).completedAt} />

        <main className="ledger">
          <div className="ledger-head">
            <h2 className="ledger-title">{s.home.ledger.title}</h2>
            <p className="note">{s.home.ledger.note}</p>
          </div>
          {worlds.map((w, wi) => {
            // Every figure in the header is about the levels, and the
            // checkpoint is in none of them — not the count, not the meter,
            // not the stars, not `done`.
            //
            // This is rule 1 in arithmetic. A checkpoint never blocks, and a
            // world whose denominator included one would never read as
            // finished until the exam was passed: the section would refuse to
            // fold, the page would stop shortening as the learner got further
            // into it, and the bar under a chapter they had genuinely
            // completed would sit short of the end. The layout would be
            // saying "not done yet" about work that is done, which is a gate
            // built out of counting.
            //
            // So the header measures practice, which is the thing a learner
            // does level by level, and the checkpoint sits below it as a
            // separate offer. What it is worth is recorded elsewhere, by the
            // badge, which is the only place it should be worth anything.
            const cleared = w.missions.filter((m) => levelProgress(progress, m.id).completed).length;
            const stars = w.missions.reduce((sum, m) => sum + levelProgress(progress, m.id).stars, 0);
            // The one place the checkpoint does count. `here` is "the learner
            // is in this chapter", and it keeps the section open against the
            // fold rules — so a world cleared to the last level, whose
            // checkpoint is the next thing the learner can play, must not
            // fold shut over the row that says so.
            const here =
              next !== null && (w.missions.some((m) => m.id === next.id) || w.checkpoint?.id === next.id);
            const folded = worldIsFolded(openWorlds, w.name, cleared, w.missions.length, here);
            const panelId = `world-${w.start}`;
            const pct = w.missions.length > 0 ? (cleared / w.missions.length) * 100 : 0;
            const done = cleared === w.missions.length && w.missions.length > 0;
            // `w.name` is the English `world:` from the mission files and it
            // stays that way — it is the key three other places join on
            // (docs/i18n.md §6.1). The catalogue is where it becomes a name
            // somebody reads.
            const chapter = s.home.worlds[w.name];
            const worldName = chapter?.name ?? w.name;
            const tagline = chapter?.tagline;
            const opener = openerForWorld(openers, w.name);
            /* A chapter every level of which is cleared folds to its heading,
               and that rule now has something new behind it. A learner who
               finishes the last level of Recovery unlocks its checkpoint in
               the same instant the section closes over it, so the one moment
               it becomes available is the one moment it disappears — the
               fault the whole rearrangement was meant to fix, moved from the
               foot of the page to inside a folded drawer.

               The fold stays. It is right, and the page shortening as the
               learner gets further into it is worth keeping. What the folded
               heading gains is a mark saying the drawer is not empty.

               Only when the checkpoint is genuinely playable. A locked one is
               an advertisement, and the heading of a chapter somebody has
               just finished is not the place to put one — the offer block at
               the foot of the ledger already makes that argument, once, where
               a reader can act on it. */
            const waiting =
              w.checkpoint !== null &&
              !levelProgress(progress, w.checkpoint.id).completed &&
              gateFor(progress, w.checkpoint, missions, account.user !== null).kind === 'open';
            // One row, whichever kind it is. The gate, the stars and the XP
            // are asked exactly the same questions of a checkpoint as of a
            // level — `gateFor` reads `locked` and `requires` and has never
            // needed to know what track it is looking at, and `FREE_THROUGH`
            // already prices the checkpoint track alongside the general one
            // (scripts/build-missions.mjs). Only the drawing differs, and
            // that is the row's own business.
            const rowFor = (m: PublicMission, checkpoint: boolean) => {
              const lp = levelProgress(progress, m.id);
              // Three states, not two: open, not reached yet, or not bought.
              // The row needs to tell the last two apart, because they are
              // different refusals with different fixes.
              const gate = gateFor(progress, m, missions, account.user !== null);
              const unlocked = gate.kind === 'open';
              return (
                <LevelRow
                  key={m.id}
                  mission={m}
                  checkpoint={checkpoint}
                  stars={lp.stars}
                  cleared={lp.completed}
                  unverified={lp.completed && !lp.verified}
                  unverifiedWhy={lp.flags?.map((f) => f.detail).join('; ')}
                  skipped={lp.skipped === true}
                  locked={!unlocked}
                  paywalled={gate.kind === 'paywall'}
                  paidSpan={paid}
                  current={!lp.completed && unlocked && next?.id === m.id}
                  xpLabel={lp.completed ? s.home.row.xpEarned(lp.xpEarned) : s.home.row.xpOffered(m.xp)}
                  reduced={reduced}
                  onLocked={onLocked}
                />
              );
            };
            const rows = w.missions.map((m) => rowFor(m, false));
            // Last, and inside the world rather than after it: the thing you
            // may do once the chapter is behind you.
            if (w.checkpoint) rows.push(rowFor(w.checkpoint, true));

            return (
              <section
                className={['world', folded ? 'is-folded' : '', done ? 'is-done' : ''].filter(Boolean).join(' ')}
                key={`${w.name}-${w.start}`}
              >
                <header className="world-head">
                  <span className="world-idx">{pad2(wi + 1)}</span>
                  <h3 className="world-name">
                    <button
                      type="button"
                      className="world-toggle"
                      aria-expanded={!folded}
                      aria-controls={panelId}
                      onClick={() => toggleWorld(w.name, folded)}
                    >
                      <span className="world-chev" aria-hidden="true" />
                      {worldName}
                    </button>
                  </h3>
                  <span className="world-rule" aria-hidden="true" />
                  <span className="world-count">
                    {s.home.world.count(cleared, w.missions.length)}
                    {/* The glyph, not the word: a reader hearing "9 black
                        star" learns nothing the meter beside it has not
                        already said with a number. */}
                    {stars > 0 && (
                      <span className="world-stars" aria-hidden="true">
                        {s.home.world.starCount(stars)}
                      </span>
                    )}
                    {/* Only on a folded heading. Open, the row two lines down
                        says this better than a mark can, and a chapter cannot
                        be told twice in one glance without one of the two
                        becoming noise.

                        The same ◆ the row uses, so the mark and the thing it
                        stands for are recognisably one object, with the
                        sentence beside it for anyone not reading shapes. */}
                    {waiting && folded && (
                      <span className="world-waiting" title={s.home.world.checkpointWaiting(worldName)}>
                        <span aria-hidden="true">◆</span>
                        <span className="sr-only">{s.home.world.checkpointWaiting(worldName)}</span>
                      </span>
                    )}
                  </span>
                  {/* The same bar the rank meter is drawn with, at a
                      thumbnail's width. It stays on a folded world on
                      purpose: folding is closing a drawer, not hiding a
                      result. */}
                  <span className="world-meter">
                    <Meter pct={pct} label={s.home.world.meterLabel(worldName, cleared, w.missions.length)} />
                  </span>
                </header>
                <div id={panelId} hidden={folded}>
                  {tagline && <p className="world-tag">{tagline}</p>}
                  {/* The chapter's introduction, above the levels rather than
                      among them: it is not a level, it cannot be cleared, and
                      it must never read as something standing between the
                      learner and the next thing they can play. A world with
                      no opener written simply has no card, which is how this
                      shipped with two of fourteen and looked deliberate. */}
                  {opener && (
                    <a
                      className={['world-opener', openerSeen(opener.slug) ? 'is-read' : ''].filter(Boolean).join(' ')}
                      href={`?opener=${encodeURIComponent(opener.slug)}`}
                    >
                      <span className="world-opener-label">{s.home.world.openerLabel}</span>
                      <span className="world-opener-title">{opener.title}</span>
                      <span className="world-opener-lede">{opener.lede}</span>
                      <span className="world-opener-read">{s.home.world.openerMinutes(opener.minutes)}</span>
                    </a>
                  )}
                  {reduced ? (
                    <ol className="levels">{rows}</ol>
                  ) : (
                    <motion.ol
                      className="levels"
                      variants={listVariants(w.start, step)}
                      initial="initial"
                      animate="animate"
                    >
                      {rows}
                    </motion.ol>
                  )}
                </div>
              </section>
            );
          })}
        </main>

        {/* The rest of the course, and what it costs. Directly under the
            ledger and above the credibility bands, which is the order the
            argument is actually made in: here is everything you cleared for
            nothing, here is the rest of it and the price, and here is why you
            can believe the people who wrote it. It draws nothing for an
            account that already owns the whole course (components/home/
            Offer.tsx), so a paying learner never sees a sales block. */}
        {/* The account's own period, when it has had one: when it ends, and
            the renewal in its last fortnight. Above the offer, which is back
            only once a period has lapsed (components/home/TermNote.tsx). */}
        <PreviewResults key={account.user?.id ?? 'guest'} missions={missions} progress={progress} userId={account.user?.id ?? null} upgradeHref={offer?.price ? '#pricing' : '/?view=course'} />
        <TermNote account={account} version={missions} onUnlock={onUnlock} />
        <Offer
          missions={missions}
          account={account}
          offer={offer}
          place="ledger"
          certificate={closest}
          onUnlock={onUnlock}
          onInvite={onInvite}
        />

        <footer className="foot">
          <span>{s.home.footer.fiction}</span>
          <span className="note">
            {t.unverified > 0 ? s.home.footer.unverifiedNote : s.home.footer.starsNote}
          </span>
          {/* The three documents. Absolute paths, not `?` routes, and that is
              the point: they are static files built outside the bundle
              (scripts/build-legal.mjs), so the delegated click handler in
              App.tsx ignores them — it claims only query-string hrefs — and
              the browser navigates properly. They have to be readable by
              somebody who is not signed in, has no account and may have
              scripting off, which is nothing an in-app screen can promise.

              The suffix follows the reader's language rather than the toggle:
              a page with no bundle cannot listen to a toggle, so the link
              picks the file and each page carries a link to its counterpart. */}
          <nav className="foot-legal">
            {/* The pitch, which this page no longer carries. An ordinary path
                rather than a `?` route — the landing page mounts instead of
                this app and boots no virtual machine (lib/landing.ts), so the
                delegated handler in App.tsx must not claim it. It is here
                rather than in the bar because a learner wants it roughly
                never, and when they do it is to send it to somebody. */}
            <a href={ABOUT_PATH}>{s.home.footer.about}</a>
            <a href={legalHref('terms', locale)}>{s.home.footer.legal}</a>
            <a href={legalHref('privacy', locale)}>{s.home.footer.privacy}</a>
            <a href={legalHref('refunds', locale)}>{s.home.footer.refunds}</a>
          </nav>
        </footer>
      </div>
    </MotionConfig>
  );
}

export default Ledger;
